import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  CapabilityResponseDataSchema,
  RequestReviewCapabilityConfigSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { RequestReviewFields } from "./RequestReviewFields"

type RequestReviewConfig = z.infer<typeof RequestReviewCapabilityConfigSchema>

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: RequestReviewConfig
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function RequestReviewForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const [config, setConfig] = useState<RequestReviewConfig>({
    providers: initialConfig?.providers ?? [],
    definition: initialConfig?.definition ?? "",
    selection: initialConfig?.selection ?? { strategy: "auto" },
    isWorkflowOnly: initialConfig?.isWorkflowOnly ?? false,
  })

  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  // Reset selection to auto when dropping to 0 or 1 provider (selection only matters with 2+)
  useEffect(() => {
    if (config.providers.length <= 1 && config.selection.strategy !== "auto") {
      setConfig((prev) => ({
        ...prev,
        selection: { strategy: "auto" },
      }))
    }
  }, [config.providers.length, config.selection.strategy])

  // Auto-clear forcedProviderId when the selected provider is no longer configured
  useEffect(() => {
    const { strategy, forcedProviderId } = config.selection
    if (strategy === "force_provider" && forcedProviderId) {
      const providerIds = config.providers.map((p) => p.id)
      if (!providerIds.includes(forcedProviderId)) {
        setConfig((prev) => ({
          ...prev,
          selection: { ...prev.selection, forcedProviderId: undefined },
        }))
      }
    }
  }, [config.providers, config.selection])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      if (config.providers.length === 0) {
        toast.error(t("capabilities.catalog.RequestReview.config.providers.minRequired"))
        return
      }

      const googleMapsWithoutLabel = config.providers.filter(
        (p) => p.type === "google_maps" && !p.label?.trim(),
      )
      if (
        googleMapsWithoutLabel.length > 0 &&
        config.providers.filter((p) => p.type === "google_maps").length > 1
      ) {
        toast.error(
          t("capabilities.catalog.RequestReview.config.providers.googleMaps.venue.required"),
        )
        return
      }

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: CapabilityIdentifier.RequestReview,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t("capabilities.catalog.RequestReview.name"),
          }),
        )
      } else {
        await updateCapabilityConfig({ id: capability.id, config })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      config,
      capability.id,
      installCapability,
      updateCapabilityConfig,
      onConfigure,
      onClose,
      t,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Workflow-only toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="workflow-only">
            {t("capabilities.catalog.RequestReview.config.workflowOnly.label")}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t("capabilities.catalog.RequestReview.config.workflowOnly.description")}
          </p>
        </div>
        <Switch
          id="workflow-only"
          checked={config.isWorkflowOnly ?? false}
          onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, isWorkflowOnly: checked }))}
        />
      </div>

      {/* Definition — hidden when workflow-only */}
      {!config.isWorkflowOnly && (
        <div className="flex flex-col gap-1.5">
          <Label>{t("capabilities.catalog.RequestReview.config.definition.label")}</Label>
          <Textarea
            value={config.definition}
            onChange={(e) => setConfig((prev) => ({ ...prev, definition: e.target.value }))}
            placeholder={t("capabilities.catalog.RequestReview.config.definition.placeholder")}
            rows={3}
            maxLength={1536}
          />
          <p className="text-sm text-muted-foreground">
            {t("capabilities.catalog.RequestReview.config.definition.description")}
          </p>
        </div>
      )}

      {/* Providers */}
      <RequestReviewFields
        value={{ providers: config.providers }}
        onChange={({ providers }) => setConfig((prev) => ({ ...prev, providers }))}
      />

      {/* Selection strategy — only relevant when multiple providers are configured */}
      {config.providers.length > 1 && (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">
            {t("capabilities.catalog.RequestReview.config.selection.title")}
          </p>
          <div className="flex flex-col gap-1.5">
            <Label>{t("capabilities.catalog.RequestReview.config.selection.strategy.label")}</Label>
            <Select
              value={config.selection.strategy}
              onValueChange={(value) =>
                setConfig((prev) => ({
                  ...prev,
                  selection: { ...prev.selection, strategy: value as "auto" | "force_provider" },
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">
                  {t("capabilities.catalog.RequestReview.config.selection.strategy.options.auto")}
                </SelectItem>
                <SelectItem value="force_provider">
                  {t(
                    "capabilities.catalog.RequestReview.config.selection.strategy.options.force_provider",
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {config.selection.strategy === "force_provider" && (
            <div className="flex flex-col gap-1.5">
              <Label>
                {t("capabilities.catalog.RequestReview.config.selection.forcedProvider.label")}
              </Label>
              {config.providers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "capabilities.catalog.RequestReview.config.selection.forcedProvider.noProviders",
                  )}
                </p>
              ) : (
                <Select
                  value={config.selection.forcedProviderId ?? ""}
                  onValueChange={(value) =>
                    setConfig((prev) => ({
                      ...prev,
                      selection: { ...prev.selection, forcedProviderId: value },
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "capabilities.catalog.RequestReview.config.selection.forcedProvider.placeholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {config.providers.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.type === "google_maps"
                          ? `Google Maps — ${provider.label || t("capabilities.catalog.RequestReview.config.selection.forcedProvider.unnamedVenue")}`
                          : "Trustpilot"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
